import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Form,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextInput,
} from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import type { ApiResponseClientList } from '../../../types/api/ApiResponseClient';
import type {
  BackupAttributionCandidate,
  BackupVmMapping,
  BackupVmMappingCreateRequest,
  BackupVmMappingUpdateRequest,
} from '../../../types/models/BackupVmMapping';
import type { ApiResponseBackupVmMappingSingle } from '../../../types/api/ApiResponseBackupVmMapping';
import ClientTypeahead, { type ClientOption } from '../ProductsAndPrices/ClientTypeahead';
import { SuggestionStatusLabel, suggestionProposesClient } from './backupSuggestionLabels';
import { attributionSavedNotification } from './attributionMessages';

const NOTE_MAX_LENGTH = 255;

// Backend field-error keys this form renders inline, on the matching input. Everything else in the
// `data` map (backup_mapping_id, hypervisor, vm_name, body, id, …) is surfaced in the form-level
// alert — those identify the immutable triple, which this form never lets the user edit.
const FIELD_ERROR_KEYS = new Set(['clientID', 'note']);

interface BackupAttributionModalProps {
  /** Create mode: the queue row being attributed. Mutually exclusive with `mapping`. */
  candidate: BackupAttributionCandidate | null;
  /** Edit mode: the existing override being re-attributed or re-annotated. */
  mapping: BackupVmMapping | null;
  /** `<backup target> / <customer directory>` when known, else a `Directory #<id>` fallback. */
  directoryLabel: string;
  onClose: () => void;
  /**
   * Hand the saved row back to the page: `previous` is the row as it was before an edit (null on
   * create) and `subjectRestamped` is the backend's affected-rows count — `0` means the triple
   * matched no reported backup.
   */
  onSaved: (saved: BackupVmMapping, previous: BackupVmMapping | null, subjectRestamped: number) => void;
}

/**
 * Attribute one backed-up VM to a client (create), or re-attribute/re-annotate an existing
 * override (edit). One component, two modes, mirroring `CertificatePlatformModal`.
 *
 * The identity triple (`backup_mapping_id` / `hypervisor` / `vm_name`) is READ-ONLY in both modes.
 * The backend *rejects* those fields on PATCH rather than ignoring them, because editing one would
 * move the override to a different VM while silently reverting the previous subject on the next
 * report — re-pointing an override is delete + create.
 *
 * The parent mounts this component only while the modal is open, so every open starts from a fresh
 * mount and the client-list fetch below never runs on page load. That last part matters: `GET
 * /clients` FAILS during an active impersonation session, so fetching it eagerly would break the
 * whole page for a staff member who happens to be impersonating. Here it degrades to an inline
 * notice with the suggestion path still usable.
 */
function BackupAttributionModal({ candidate, mapping, directoryLabel, onClose, onSaved }: BackupAttributionModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();

  const isEdit = mapping !== null;
  const hypervisor = mapping?.hypervisor ?? candidate?.hypervisor ?? '';
  const vmName = mapping?.vm_name ?? candidate?.vm_name ?? '';
  const backupMappingId = mapping?.backup_mapping_id ?? candidate?.backup_mapping_id ?? null;

  const [clients, setClients] = useState<ClientOption[]>([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [clientsError, setClientsError] = useState<string | null>(null);

  // Pre-select the suggested client when opening from a row that has one — but as a starting
  // point, never as a decision: staff can change or clear it before saving.
  const [clientId, setClientId] = useState<number | null>(mapping?.clientID ?? candidate?.suggestion.clientID ?? null);
  const [note, setNote] = useState(mapping?.note ?? '');

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // `apiFetch` gets a NEW identity on every OIDC silent renew, so an effect keyed on it re-runs
  // mid-edit. Fetch the client list exactly once per mount.
  const hasFetchedClients = useRef(false);

  const fetchClients = useCallback(async () => {
    setClientsLoading(true);
    setClientsError(null);
    try {
      const response = await apiFetch('/clients');
      // Branch on the JSend `body.status`, NEVER `response.ok`: the backend maps controller
      // exceptions to `status: 'error'` at HTTP 200, so `response.ok` can be true on a failure.
      const body = (await response.json().catch(() => null)) as ApiResponseClientList | null;
      if (body?.status === 'success' && body.data?.clients) {
        setClients(body.data.clients.map((client) => ({ id: client.id, name: client.name })));
      } else {
        // The impersonation case lands here with the backend's own explanation ("Listing all
        // clients is not available during an impersonation session").
        setClientsError(body?.message ?? 'The client list could not be loaded.');
      }
    } catch (err) {
      setClientsError(err instanceof Error ? err.message : 'The client list could not be loaded.');
    } finally {
      setClientsLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (hasFetchedClients.current) {
      return;
    }
    hasFetchedClients.current = true;
    void fetchClients();
  }, [fetchClients]);

  const selectedClientName = useMemo(() => {
    const match = clients.find((client) => client.id === clientId);
    if (match !== undefined) {
      return match.name;
    }
    if (isEdit && mapping !== null && mapping.clientID === clientId) {
      return mapping.client_name;
    }
    if (candidate !== null && candidate.suggestion.clientID === clientId) {
      return candidate.suggestion.client_name;
    }
    return null;
  }, [clients, clientId, isEdit, mapping, candidate]);

  const trimmedNote = note.trim();
  const noteTooLong = trimmedNote.length > NOTE_MAX_LENGTH;

  // An empty PATCH change-set is rejected by the backend ("at least one field"), so Save stays
  // disabled until something actually differs from the loaded row.
  const hasChanges =
    mapping === null || clientId !== mapping.clientID || trimmedNote !== (mapping.note ?? '').trim();

  const canSubmit = !isSaving && clientId !== null && !noteTooLong && hasChanges && backupMappingId !== null;

  const clearFieldError = (key: string) => {
    setFieldErrors((prev) => {
      if (!(key in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const splitFieldErrors = (
    body: ApiResponseBackupVmMappingSingle | null,
  ): { errors: Record<string, string>; general: string | null } => {
    const errors: Record<string, string> = {};
    const generalParts: string[] = [];
    if (body?.data) {
      for (const [key, value] of Object.entries(body.data)) {
        if (typeof value !== 'string') {
          continue; // skips backup_vm_mapping (object) and subject_restamped (number)
        }
        if (FIELD_ERROR_KEYS.has(key)) {
          errors[key] = value;
        } else {
          generalParts.push(value);
        }
      }
    }
    let general = generalParts.length > 0 ? generalParts.join(' ') : null;
    if (general === null && Object.keys(errors).length === 0) {
      general = body?.message ?? 'Could not save the attribution. Please try again.';
    }
    return { errors, general };
  };

  const buildCreateBody = (): BackupVmMappingCreateRequest => {
    const body: BackupVmMappingCreateRequest = {
      backup_mapping_id: backupMappingId as number,
      hypervisor,
      vm_name: vmName,
      clientID: clientId as number,
    };
    if (trimmedNote !== '') {
      body.note = trimmedNote;
    }
    return body;
  };

  // PATCH is partial: send only what changed, and `note: null` to clear an annotation. The triple
  // is deliberately never included — the backend rejects it.
  const buildUpdateBody = (original: BackupVmMapping): BackupVmMappingUpdateRequest => {
    const body: BackupVmMappingUpdateRequest = {};
    if (clientId !== null && clientId !== original.clientID) {
      body.clientID = clientId;
    }
    if (trimmedNote !== (original.note ?? '').trim()) {
      body.note = trimmedNote === '' ? null : trimmedNote;
    }
    return body;
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    setFieldErrors({});
    setFormError(null);
    try {
      const response = await apiFetch(
        mapping !== null ? `/admin/backup-vm-mappings/${mapping.id}` : '/admin/backup-vm-mappings',
        {
          method: mapping !== null ? 'PATCH' : 'POST',
          body: JSON.stringify(mapping !== null ? buildUpdateBody(mapping) : buildCreateBody()),
        },
      );
      // Branch on the JSend `body.status`, NEVER `response.ok`.
      const body = (await response.json().catch(() => null)) as ApiResponseBackupVmMappingSingle | null;

      if (body?.status === 'success' && body.data?.backup_vm_mapping) {
        const saved = body.data.backup_vm_mapping;
        const restamped = typeof body.data.subject_restamped === 'number' ? body.data.subject_restamped : 0;
        onSaved(saved, mapping, restamped);
        // `subject_restamped: 0` on a create is a WARNING, not a success — see
        // attributionSavedNotification, which owns that rule for this modal and the queue's
        // one-click action alike.
        addNotification(attributionSavedNotification(saved, restamped, isEdit));
        setDrawerOpen(true);
        onClose();
        return;
      }

      const { errors, general } = splitFieldErrors(body);
      setFieldErrors(errors);
      setFormError(general);
      addNotification({
        title: isEdit ? 'Could not update the attribution' : 'Could not attribute the virtual machine',
        srTitle: 'Error alert',
        description: general ?? Object.values(errors)[0] ?? 'Please review the highlighted fields.',
        variant: 'danger',
      });
      setDrawerOpen(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setFormError(message);
      addNotification({
        title: isEdit ? 'Could not update the attribution' : 'Could not attribute the virtual machine',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  const suggestion = candidate?.suggestion ?? null;

  return (
    <Modal variant="medium" isOpen onClose={onClose}>
      <ModalHeader title={isEdit ? 'Edit attribution' : 'Attribute virtual machine'} />
      <ModalBody>
        <Form
          id="backup-attribution-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (canSubmit) {
              void handleSubmit();
            }
          }}
        >
          {formError !== null && (
            <Alert variant="danger" isInline title={isEdit ? 'Update failed' : 'Attribution failed'} className="pf-v6-u-mb-md">
              {formError}
            </Alert>
          )}

          {/* The identity triple, read-only in both modes: the backend rejects any attempt to
              change it, and re-pointing an override is delete + create. */}
          <DescriptionList isHorizontal isCompact className="pf-v6-u-mb-md">
            <DescriptionListGroup>
              <DescriptionListTerm>Virtual machine</DescriptionListTerm>
              <DescriptionListDescription>{vmName}</DescriptionListDescription>
            </DescriptionListGroup>
            <DescriptionListGroup>
              <DescriptionListTerm>Hypervisor</DescriptionListTerm>
              <DescriptionListDescription>{hypervisor}</DescriptionListDescription>
            </DescriptionListGroup>
            <DescriptionListGroup>
              <DescriptionListTerm>Backup directory</DescriptionListTerm>
              <DescriptionListDescription>{directoryLabel}</DescriptionListDescription>
            </DescriptionListGroup>
            {mapping !== null && (
              <DescriptionListGroup>
                <DescriptionListTerm>Currently attributed to</DescriptionListTerm>
                <DescriptionListDescription>
                  {mapping.client_name ?? `Client #${mapping.clientID}`}
                </DescriptionListDescription>
              </DescriptionListGroup>
            )}
            {candidate !== null && (
              <DescriptionListGroup>
                <DescriptionListTerm>Currently attributed to</DescriptionListTerm>
                <DescriptionListDescription>
                  {candidate.current_client_name ?? 'Unattributed'}
                </DescriptionListDescription>
              </DescriptionListGroup>
            )}
            {suggestion !== null && suggestionProposesClient(suggestion) && (
              <DescriptionListGroup>
                <DescriptionListTerm>NetBox suggests</DescriptionListTerm>
                <DescriptionListDescription>
                  {suggestion.client_name}{' '}
                  <SuggestionStatusLabel suggestion={suggestion} hypervisor={hypervisor} />
                  {suggestion.owner_name !== null && (
                    <div className="pf-v6-u-font-size-sm">NetBox owner: {suggestion.owner_name}</div>
                  )}
                </DescriptionListDescription>
              </DescriptionListGroup>
            )}
          </DescriptionList>

          <FormGroup label="Attribute to client" isRequired fieldId="admin-override-client-input">
            {clientsError === null ? (
              <ClientTypeahead
                clients={clients}
                value={clientId}
                onChange={(id) => {
                  setClientId(id);
                  clearFieldError('clientID');
                }}
                isDisabled={isSaving || clientsLoading}
              />
            ) : (
              <Alert variant="warning" isInline title="The client list is unavailable">
                {clientsError}
                {clientId !== null ? (
                  <>
                    {' '}
                    You can still save the suggested client <strong>{selectedClientName ?? `#${clientId}`}</strong>.
                  </>
                ) : (
                  ' End the impersonation session (or reopen this dialog) to pick a client.'
                )}
              </Alert>
            )}
            {fieldErrors['clientID'] !== undefined ? (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem variant="error">{fieldErrors['clientID']}</HelperTextItem>
                </HelperText>
              </FormHelperText>
            ) : (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem>
                    This decision applies to this one VM directory and overrides whichever client its backup
                    directory is mapped to.
                  </HelperTextItem>
                </HelperText>
              </FormHelperText>
            )}
          </FormGroup>

          <FormGroup label="Note" fieldId="backup-attribution-note">
            <TextInput
              id="backup-attribution-note"
              type="text"
              value={note}
              placeholder="Why this VM belongs to that client (optional)"
              onChange={(_event, value) => {
                setNote(value);
                clearFieldError('note');
              }}
              validated={fieldErrors['note'] !== undefined || noteTooLong ? 'error' : 'default'}
              isDisabled={isSaving}
            />
            <FormHelperText>
              <HelperText>
                <HelperTextItem variant={fieldErrors['note'] !== undefined || noteTooLong ? 'error' : 'default'}>
                  {fieldErrors['note'] ??
                    (noteTooLong
                      ? `A note may be at most ${NOTE_MAX_LENGTH} characters (currently ${trimmedNote.length}).`
                      : `Optional, ${NOTE_MAX_LENGTH} characters max.${isEdit ? ' Clearing it removes the note.' : ''}`)}
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button variant="primary" type="submit" form="backup-attribution-form" isDisabled={!canSubmit} isLoading={isSaving}>
          {isEdit ? 'Save' : 'Attribute'}
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={isSaving}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default BackupAttributionModal;
